import {
  Bell,
  Check,
  ChevronRight,
  Database,
  FlaskConical,
  Mail,
  MessageCircle,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Settings2,
  Smartphone,
  Sun,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { Link, NavLink } from "react-router";

import { useShellStore } from "nazare-ui/utils";
import { Switch } from "nazare-ui/components";
import { PageSection } from "nazare-ui/components/page-section";
import {
  THEME_MODE_OPTIONS,
  type ThemeMode,
  useTheme,
} from "nazare-ui/utils/theme";

import {
  useNotificationTypesConfig,
  type NotificationTypeConfig,
} from "../notifications-admin-api";
import { useQaSchedule } from "../qa-automation-api";
import {
  useSelfNotificationChannels,
  useSelfNotificationPreferences,
  useSetSelfNotificationPreference,
} from "../settings-api";
import { useSecondaryHeader } from "../secondary-header-context";
import { useDocumentTitle } from "../use-document-title";
import styles from "./settings.module.css";

export type SettingsSection = "me" | "system";

type NotificationChannelKey = "email" | "whatsapp" | "slack" | "push" | "in_app";

const THEME_ICONS: Record<ThemeMode, LucideIcon> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
};

const NOTIFICATION_CHANNELS: Array<{
  key: NotificationChannelKey;
  label: string;
  Icon: LucideIcon;
}> = [
  { key: "email", label: "Email", Icon: Mail },
  { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle },
  { key: "slack", label: "Slack", Icon: Bell },
  { key: "push", label: "Push", Icon: Smartphone },
  { key: "in_app", label: "In app", Icon: Monitor },
];

const QA_PREFERENCES = [
  {
    type: "qa_run_scheduled",
    label: "Scheduled QA runs",
    description: "When an automatically scheduled QA run finishes.",
  },
  {
    type: "qa_run_mine",
    label: "My QA runs",
    description: "When a QA run you started finishes.",
  },
  {
    type: "qa_run_others",
    label: "Other users’ QA runs",
    description: "When a run started by somebody else finishes.",
  },
] as const;

const INFRASTRUCTURE = [
  { label: "Environments", value: "Development and production" },
  { label: "Compute", value: "ECS services and scheduled tasks" },
  { label: "Data", value: "PostgreSQL, DynamoDB and Athena" },
  { label: "Messaging", value: "SQS, email ingestion and notifications" },
];

const DELIVERY_PROVIDERS = [
  ["email", "Email", Mail],
  ["whatsapp", "WhatsApp", MessageCircle],
  ["slack", "Slack", Bell],
  ["push", "Push", Smartphone],
] as const;

function SettingsSubnav() {
  return (
    <div className={styles.subnavRow}>
      <nav className={styles.subnav} aria-label="Settings sections">
        <NavLink to="/settings/me" className={({ isActive }) => isActive ? styles.subnavLinkActive : styles.subnavLink}>
          Me
        </NavLink>
        <NavLink to="/settings/system" className={({ isActive }) => isActive ? styles.subnavLinkActive : styles.subnavLink}>
          System
        </NavLink>
      </nav>
      <span className={styles.subnavSpacer} />
    </div>
  );
}

function ChoiceButton({
  selected,
  icon,
  label,
  description,
  onClick,
}: {
  selected: boolean;
  icon: ReactNode;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={selected ? styles.choiceActive : styles.choice}
      onClick={onClick}
      aria-pressed={selected}
    >
      <span className={styles.choiceIcon}>{icon}</span>
      <span className={styles.choiceText}>
        <strong>{label}</strong>
        <span>{description}</span>
      </span>
      {selected && <Check className={styles.choiceCheck} size={15} aria-hidden="true" />}
    </button>
  );
}

function MeSettings() {
  const { mode, setMode } = useTheme();
  const navRailCollapsed = useShellStore((state) => state.navRailCollapsed);
  const setNavRailCollapsed = useShellStore((state) => state.setNavRailCollapsed);
  const channels = useSelfNotificationChannels();
  const preferences = useSelfNotificationPreferences();
  const updatePreference = useSetSelfNotificationPreference();

  const isChannelAvailable = (channel: NotificationChannelKey) => {
    if (channel === "email" || channel === "in_app") return true;
    return (channels.data?.channels ?? []).some(
      (connected) => connected.channel_type === channel && connected.verified,
    );
  };

  const isEnabled = (notificationType: string, channelType: NotificationChannelKey) =>
    (preferences.data?.preferences ?? []).some(
      (preference) =>
        preference.notification_type === notificationType &&
        preference.reference_id === null &&
        preference.channel_type === channelType &&
        preference.enabled,
    );

  return (
    <div className={styles.sectionStack}>
      <PageSection padded title="Appearance" description="Choose how PCG Platform looks on this browser.">
        <div className={styles.choiceGrid}>
          {THEME_MODE_OPTIONS.map((option) => {
            const Icon = THEME_ICONS[option.value];
            return (
              <ChoiceButton
                key={option.value}
                selected={mode === option.value}
                icon={<Icon size={17} aria-hidden="true" />}
                label={option.label}
                description={option.value === "system" ? "Follow this device" : `${option.label} at all times`}
                onClick={() => setMode(option.value)}
              />
            );
          })}
        </div>
      </PageSection>

      <PageSection padded title="Navigation" description="Set the default width of the navigation rail.">
        <div className={styles.choiceGridTwo}>
          <ChoiceButton
            selected={!navRailCollapsed}
            icon={<PanelLeftOpen size={17} aria-hidden="true" />}
            label="Expanded"
            description="Show icons and labels"
            onClick={() => setNavRailCollapsed(false)}
          />
          <ChoiceButton
            selected={navRailCollapsed}
            icon={<PanelLeftClose size={17} aria-hidden="true" />}
            label="Collapsed"
            description="Keep the rail compact"
            onClick={() => setNavRailCollapsed(true)}
          />
        </div>
      </PageSection>

      <PageSection
        padded
        title="QA notifications"
        description="Choose which platform QA events reach you through each connected channel."
        count={channels.isLoading || preferences.isLoading || channels.isError || preferences.isError ? undefined : QA_PREFERENCES.length}
      >
        {channels.isLoading || preferences.isLoading ? (
          <div className={styles.loadingState}>Loading your notification preferences…</div>
        ) : channels.isError || preferences.isError ? (
          <div className={styles.errorState}>Your notification preferences could not be loaded.</div>
        ) : (
          <div className={styles.preferenceTableWrap}>
            <table className={styles.preferenceTable}>
              <thead>
                <tr>
                  <th scope="col">Event</th>
                  {NOTIFICATION_CHANNELS.map(({ key, label, Icon }) => (
                    <th scope="col" key={key}>
                      <span className={styles.channelHeading}><Icon size={13} aria-hidden="true" />{label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {QA_PREFERENCES.map((preference) => (
                  <tr key={preference.type}>
                    <th scope="row">
                      <span className={styles.preferenceLabel}>{preference.label}</span>
                      <span className={styles.preferenceDescription}>{preference.description}</span>
                    </th>
                    {NOTIFICATION_CHANNELS.map(({ key, label }) => {
                      const available = isChannelAvailable(key);
                      const enabled = isEnabled(preference.type, key);
                      return (
                        <td key={key}>
                          <Switch
                            checked={enabled}
                            aria-label={`${preference.label}: ${label}`}
                            title={available ? `${enabled ? "Disable" : "Enable"} ${label}` : `${label} is not connected`}
                            disabled={!available || updatePreference.isPending}
                            onCheckedChange={(checked) => updatePreference.mutate({
                              notificationType: preference.type,
                              channelType: key,
                              enabled: checked,
                            })}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className={styles.preferenceFooter}>
          <span>Unavailable delivery methods must first be connected in Nazare.</span>
          {updatePreference.isError && <strong>Couldn’t save the last change. It has been restored.</strong>}
        </div>
      </PageSection>
    </div>
  );
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

type ProviderStatus = "ready" | "attention" | "notConfigured";

function providerStatus(types: NotificationTypeConfig[], provider: keyof NotificationTypeConfig["channels"]): ProviderStatus {
  const statuses = types.map((type) => type.channels[provider]?.status).filter(Boolean);
  if (statuses.some((status) => status === "active" || status === "APPROVED")) return "ready";
  if (statuses.some((status) => status && status !== "not_configured" && status !== "not_applicable")) return "attention";
  return "notConfigured";
}

const PROVIDER_STATUS_LABEL: Record<ProviderStatus, string> = {
  ready: "Ready",
  attention: "Attention",
  notConfigured: "Not configured",
};

function SystemSettings() {
  const schedule = useQaSchedule();
  const notificationTypes = useNotificationTypesConfig();
  const providerStates = DELIVERY_PROVIDERS.map(([key, label, Icon]) => ({
    key,
    label,
    Icon,
    status: providerStatus(notificationTypes.data ?? [], key),
  }));
  const readyProviderCount = providerStates.filter((provider) => provider.status === "ready").length;
  const nextRunCount = schedule.data?.nextRuns.length ?? 0;

  return (
    <div className={styles.sectionStack}>
      <PageSection padded title="QA schedule" description="Deployment-backed cadence for automated QA runs.">
        {schedule.isLoading ? (
          <div className={styles.loadingState}>Loading the QA schedule…</div>
        ) : schedule.isError ? (
          <div className={styles.errorState}>The QA schedule could not be loaded.</div>
        ) : (
          <div className={styles.scheduleLayout}>
            <div className={styles.scheduleSummary}>
              <span className={schedule.data?.enabled ? styles.statusReady : styles.statusMuted}>
                {schedule.data?.enabled ? "Enabled" : "Disabled"}
              </span>
              <strong>{schedule.data?.cron ?? "No cadence configured"}</strong>
              <span>Timezone: {schedule.data?.timezone ?? "UTC"}</span>
            </div>
            <div className={styles.nextRuns}>
              <div className={styles.fieldLabelRow}>
                <span className={styles.fieldLabel}>Next runs</span>
                <span className={styles.countBadge}>{nextRunCount}</span>
              </div>
              {schedule.data?.nextRuns.length ? (
                <ol>{schedule.data.nextRuns.map((run) => <li key={run}>{formatDateTime(run)}</li>)}</ol>
              ) : (
                <p>No upcoming runs.</p>
              )}
            </div>
          </div>
        )}
        <Link className={styles.deepLink} to="/qa-automation">
          Open QA Automation <ChevronRight size={14} aria-hidden="true" />
        </Link>
      </PageSection>

      <PageSection padded title="Notification delivery" description="Live provider readiness reported by the notification service.">
        {notificationTypes.isLoading ? (
          <div className={styles.loadingState}>Checking delivery providers…</div>
        ) : notificationTypes.isError ? (
          <div className={styles.errorState}>Delivery readiness could not be loaded.</div>
        ) : (
          <>
            <div className={styles.providerSummary}>
              <strong>{readyProviderCount}</strong>
              <span>of <b>{DELIVERY_PROVIDERS.length}</b> providers ready</span>
            </div>
            <div className={styles.providerGrid}>
              {providerStates.map(({ key, label, Icon, status }) => (
                <div className={styles.providerItem} data-status={status} key={key}>
                  <span className={styles.providerIcon}><Icon size={16} aria-hidden="true" /></span>
                  <strong>{label}</strong>
                  <span className={styles.providerStatus}>{PROVIDER_STATUS_LABEL[status]}</span>
                </div>
              ))}
            </div>
          </>
        )}
        <Link className={styles.deepLink} to="/notifications">
          Open Notifications <ChevronRight size={14} aria-hidden="true" />
        </Link>
      </PageSection>

      <PageSection padded title="Infrastructure catalogue" description="Configuration snapshot from the current Nazare platform stack.">
        <div className={styles.infrastructureList}>
          {INFRASTRUCTURE.map((item) => (
            <div key={item.label}><span>{item.label}</span><strong>{item.value}</strong></div>
          ))}
        </div>
        <div className={styles.deepLinkRow}>
          <Link className={styles.deepLink} to="/workflows"><Workflow size={14} aria-hidden="true" /> Workflows</Link>
          <Link className={styles.deepLink} to="/data-explorer"><Database size={14} aria-hidden="true" /> Data Explorer</Link>
          <Link className={styles.deepLink} to="/qa-automation"><FlaskConical size={14} aria-hidden="true" /> QA Automation</Link>
        </div>
      </PageSection>
    </div>
  );
}

export function SettingsPage({ section }: { section: SettingsSection }) {
  useDocumentTitle(section === "me" ? "Settings — Me" : "Settings — System");
  const subnav = useMemo(() => <SettingsSubnav />, []);
  useSecondaryHeader(subnav);

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <span className={styles.introIcon}><Settings2 aria-hidden="true" /></span>
        <div>
          <h1>Settings</h1>
          <p>{section === "me" ? "Your experience and platform notifications." : "Read-only system configuration and service readiness."}</p>
        </div>
      </div>
      {section === "me" ? <MeSettings /> : <SystemSettings />}
    </div>
  );
}
